const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "14 Mar" */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "14 Mar, 1:28 pm" — 12-hour with lowercase am/pm */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const month = MONTHS[d.getMonth()];
  let hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month}, ${hours}:${mins} ${ampm}`;
}

/** "14 Mar, 13:28" — 24-hour clock (pairs with formatDateTime, the 12h am/pm form) */
export function formatDateTime24(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const month = MONTHS[d.getMonth()];
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${day} ${month}, ${hours}:${mins}`;
}

/** "13:28" — 24-hour clock, time only. */
export function formatTime24(iso: string): string {
  const d = new Date(iso);
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${hours}:${mins}`;
}

/** "14/03" — compact day/month */
export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}

/** "Apr 29" — month-day, no year. For dense admin tables where the year is
 *  redundant (caller can hover or click for full date if needed). */
export function formatDateMonthDay(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Jul 19, 20:12" — month-day with a 24-hour time, no year and no seconds.
 *  The `formatDateMonthDay` + `formatTime24` pairing as one label, for tables
 *  listing recent events where the minute matters but the year doesn't. */
export function formatMonthDayTime24(iso: string): string {
  const d = new Date(iso);
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${hours}:${mins}`;
}

/** "09/30 11:43" — numeric month/day with a 24-hour time. For narrow list
 *  columns: fixed width, so a column of them lines up. */
export function formatMonthDaySlashTime24(iso: string): string {
  const d = new Date(iso);
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${month}/${day} ${hours}:${mins}`;
}

/** "14 Mar 2026" */
export function formatDateWithYear(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Apr 20, 18:32:14" — short month-day, 24-hour with seconds. For admin
 *  detail pages where the second-level timestamp matters (run latency,
 *  ordering, debugging). */
export function formatDateTimePrecise(iso: string | Date): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  const day = d.getDate().toString().padStart(2, '0');
  const month = MONTHS[d.getMonth()];
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  const s = d.getSeconds().toString().padStart(2, '0');
  return `${month} ${day}, ${h}:${m}:${s}`;
}

/**
 * Format a list of ISO datetimes for compact display, dropping whatever is
 * redundant across the whole list. Order is preserved (result[i] ↔ isos[i]):
 *   - every date distinct  → date only   ("Jul 15")
 *   - every date the same  → time only   ("18:20")
 *   - otherwise (mixed)    → date + time ("Jul 15 18:20")
 * Override the renderings via `dateFormat` / `timeFormat` (default: month-day
 * and 24-hour clock).
 */
export function formatDateTimeList(
  isos: string[],
  dateFormat: (iso: string) => string = formatDateMonthDay,
  timeFormat: (iso: string) => string = formatTime24,
): string[] {
  const uniqueDays = new Set(isos.map(iso => new Date(iso).toDateString())).size;
  const dateOnly = uniqueDays === isos.length; // no two share a date
  const timeOnly = uniqueDays <= 1;            // all on the same date
  return isos.map(iso =>
    dateOnly ? dateFormat(iso)
      : timeOnly ? timeFormat(iso)
        : `${dateFormat(iso)} ${timeFormat(iso)}`,
  );
}

/**
 * "14 Mar" / "14 Mar, 6:00 PM" — an assignment deadline (`dueAt`), showing the
 * time only when one was set. Never `new Date(dueAt)`: a date-only deadline
 * parses as UTC midnight, which renders as the previous day east of Greenwich.
 * See docs/FeatureDesigns/assignments-tests/assignment-due-time.md.
 */
export function formatDue(dueAt: string): string {
  const [, m, d] = dueAt.slice(0, 10).split('-').map(Number);
  const date = `${d} ${MONTHS[m - 1]}`;
  const time = dueAt.length > 10 ? dueAt.slice(11, 16) : undefined;
  if (!time) return date;
  const [hh, mm] = time.split(':').map(Number);
  const suffix = hh >= 12 ? 'PM' : 'AM';
  return `${date}, ${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${suffix}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}
